/**
 * A formal CAPA — corrective and preventive action (ISO/IEC 17025 §8.7).
 *
 * The CAPA governs; Work Orders do the work. It holds the owner, the
 * investigation's conclusion, the approved plan and the effectiveness check.
 * The investigation itself, and each approved action, are Work Orders raised
 * from it — they carry the steps, the evidence and the performer.
 *
 *   open → investigating → root_cause_review → planning → plan_review
 *        → in_progress → effectiveness_watch → closed
 *
 * A repeat during the effectiveness watch moves it to `reopened`, and a new
 * investigation starts from there.
 */

export type CapaStatus =
  | "open"
  | "investigating"
  | "root_cause_review"
  | "planning"
  | "plan_review"
  | "in_progress"
  | "effectiveness_watch"
  | "closed"
  | "reopened";

export type CapaTriggerKind =
  | "repeat"
  | "severity"
  | "no_known_fix"
  | "correction_failed"
  | "qc_trend"
  | "manual";

export type CapaRisk = "low" | "medium" | "high";

export interface CapaCandidateCause {
  id: string;
  description: string;
  /** How likely the catalog thinks this cause is — a starting hypothesis, not a finding. */
  likelihood?: number;
}

export type RcaMethod = "5why" | "fishbone" | "other";

export interface CapaRootCause {
  /** The candidate it confirms, or absent when the investigation found a new cause. */
  causeId?: string;
  statement: string;
  method: RcaMethod;
  whys: string[];
  concludedBy: string;
  concludedAt: string;
  signedOffBy?: string;
  signedOffAt?: string;
}

export type CapaActionKind = "correction" | "corrective" | "preventive";

export interface CapaPlanAction {
  id: string;
  kind: CapaActionKind;
  description: string;
  assignee: string;
  dueAt?: string;
  sopId?: string;
  /** Set once the plan is approved and the action's Work Order is raised. */
  workRecordId?: string;
  /**
   * The action's last Work Order was closed without approval, so the action was
   * not done. Shown while re-planning: drop the action or approve it again to
   * raise a new Work Order.
   */
  cancelledRecord?: {
    workRecordId: string;
    reason: string;
    note: string;
    by: string;
  };
}

export interface CapaEffectivenessCriteria {
  /** How long to watch for a repeat after the last action closes. */
  windowDays: number;
  /** Repeats tolerated in the window. 0 means any repeat reopens the CAPA. */
  maxRecurrences: number;
}

export interface CapaPlan {
  actions: CapaPlanAction[];
  criteria: CapaEffectivenessCriteria;
  submittedBy?: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface CapaEffectiveness {
  from: string;
  until: string;
  recurrenceEventIds: string[];
  result: "watching" | "passed" | "failed";
}

export interface CapaHistoryEntry {
  at: string;
  by: string;
  role: string;
  event: string;
  from: CapaStatus;
  to: CapaStatus;
  note?: string;
}

export interface CapaRecord {
  id: string;
  title: string;
  errorCode: string;
  instrument: string;
  trigger: { kind: CapaTriggerKind; note?: string; workRecordId?: string };
  owner: string;
  dueAt: string;
  risk: CapaRisk;
  status: CapaStatus;
  candidateCauses: CapaCandidateCause[];
  /** The current investigation. Earlier ones (before a reopen) stay in `pastInvestigationIds`. */
  investigationRecordId?: string;
  pastInvestigationIds?: string[];
  /** The GOV-EQ-01 Work Order that returns a contained instrument to service. */
  returnToServiceRecordId?: string;
  rootCause?: CapaRootCause;
  plan: CapaPlan;
  effectiveness?: CapaEffectiveness;
  closedBy?: string;
  closedAt?: string;
  history: CapaHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export const CAPA_STATUS_LABELS: Record<CapaStatus, string> = {
  open: "Open",
  investigating: "Investigating",
  root_cause_review: "Root cause in review",
  planning: "Planning actions",
  plan_review: "Plan in review",
  in_progress: "Actions in progress",
  effectiveness_watch: "Effectiveness watch",
  closed: "Closed",
  reopened: "Reopened",
};

export const CAPA_TRIGGER_LABELS: Record<CapaTriggerKind, string> = {
  repeat: "Repeating error",
  severity: "High-severity error",
  no_known_fix: "No known fix",
  correction_failed: "A correction did not hold",
  qc_trend: "Repeated QC failures",
  manual: "Raised manually",
};

export const CAPA_ACTION_KIND_LABELS: Record<CapaActionKind, string> = {
  correction: "Correction",
  corrective: "Corrective",
  preventive: "Preventive",
};

export const OPEN_CAPA_STATUSES: CapaStatus[] = [
  "open",
  "investigating",
  "root_cause_review",
  "planning",
  "plan_review",
  "in_progress",
  "effectiveness_watch",
  "reopened",
];
